import { ParseDisabledOptions } from '../utils/parseDisabled'

type GenericReason = {
  /**
   * flexible custom reason for disabling to be used when one of the pre-set
   * reasons doesn't suit the condition
   *
   * message will be displayed as-is in tooltip
   */
  why: 'disabled'
  message: string
}

type InactiveStatus = {
  /**
   * disabled due to the associated record being inactive/soft-deleted
   *
   * `entityName` will be inserted into the following message template and
   * displayed in a tooltip
   *
   * @default 'record'
   * @example
   * `This field cannot be edited while its associated ${entityName ?? 'record'} is inactive`
   */
  why: 'inactive'
  entityName?: string
}

type InvalidStatus = {
  /**
   * disabled due to associated record having a status that does not allow this
   * field to be edited
   *
   * `statusName` will be inserted into the following message template and
   * displayed in a tooltip
   *
   * @example
   * `This field cannot be edited while in status: ${statusName}`
   */
  why: 'invalid status'
  statusName: string
}

type OnlyStatus = {
  /**
   * disabled due to the associated record _not_ being in it's only valid status
   *
   * `statusName` will be inserted into the following message template and
   * displayed in a tooltip
   *
   * @example
   * `This field can only be edited while in status: ${statusName}`
   */
  why: 'only status'
  statusName: string
}

export type DisabledReason =
  | 'loading'
  | 'missing permission'
  | 'no changes'
  | InactiveStatus
  | InvalidStatus
  | OnlyStatus
  | GenericReason

export type DisabledOptions = {
  /**
   * whether or not to display the tooltip
   */
  disabled: boolean
  /**
   * content of the tooltip, the reason the form item has been disabled
   */
  reason: DisabledReason
}

export type WithDisabled = {
  /**
   * for handling disabled states and their associated tooltip messages
   *
   * usually passed into the `DisabledTooltip` component
   *
   * pass into `parseDisabled` to generate tooltip content
   *
   * pass into {@link useDisabledOptions} to get resolved options and access to
   * `some` and `every` boolean states
   */
  disabled?: ParseDisabledOptions
}

type UseDisabledOptions = [
  DisabledOptions[],
  {
    /**
     * All options are disabled
     *
     * For most situations use {@link some}
     */
    every: boolean
    /**
     * At least one option is disabled
     *
     * For most situations, you'll use this
     */
    some: boolean
  }
]

/**
 * Resolves {@link options} into a tuple where the first value is the options
 * as defined, and the second value is an object containing the total disabled
 * state of those options
 *
 * @param options {@link ParseDisabledOptions}
 * @example
 * const [disabledOptions, { some }] = useDisabledOptions([...options])
 * return (
 *   <DisabledTooltip disabled={disabledOptions}>
 *     <Input disabled={some} />
 *   </DisabledTooltip>
 * )
 *
 * @see {@link UseDisabledOptions}
 */
export function useDisabledOptions(
  options?: ParseDisabledOptions
): UseDisabledOptions {
  const resolved = (Array.isArray(options) ? options : [options]).reduce(
    (options, option) => {
      if (option) options.push(option)
      return options
    },
    Array<DisabledOptions>()
  )
  return [
    resolved,
    {
      every: resolved.every(({ disabled }) => disabled),
      some: resolved.some(({ disabled }) => disabled),
    },
  ]
}
